import type { V2Scene } from "../types";

type AudioLayer = "water" | "birds" | "boatWood" | "wind" | "music" | "seal";

const sceneMix: Record<V2Scene, Partial<Record<AudioLayer, number>>> = {
  intro: { water: .7, birds: .42 }, question: { water: .65, boatWood: .24 }, first: { water: .58, music: .16 },
  struggle: { water: .62, wind: .36, music: .18 }, dedication: { water: .28, wind: .12 }, reflection: { water: .18 }, theory: { water: .18 },
  jiaxing: { water: .3, music: .38 }, action: { water: .3, music: .48 }, ticket: { water: .34, music: .58, seal: 1 },
  lights: { water: .18, music: .36 }, end: { water: .12, music: .22 },
};

/**
 * 全部音效都是本地合成的极短"拟音"，不用任何素材文件：
 * - tap        水珠/木船轻响（选项、提交）
 * - whoosh     过场时湖风掠过
 * - playSeal   船票盖章
 * 默认关闭（首次点击"声音"后开启），失败时静默降级。
 */
export class AudioManager {
  private context: AudioContext | null = null;
  private enabled = false;
  private scene: V2Scene = "intro";
  private noiseBuffer: AudioBuffer | null = null;

  setEnabled(enabled: boolean) { this.enabled = enabled; if (enabled) this.ensureContext(); }
  setScene(scene: V2Scene) { this.scene = scene; void sceneMix[this.scene]; }

  /** 选项落定/句子交出：一声很轻的"咚"（水珠或船木） */
  tap() {
    if (!this.enabled) return;
    const context = this.ensureContext();
    if (!context) return;
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(300, now);
    oscillator.frequency.exponentialRampToValueAtTime(118, now + .12);
    gain.gain.setValueAtTime(.0001, now);
    gain.gain.exponentialRampToValueAtTime(.05, now + .012);
    gain.gain.exponentialRampToValueAtTime(.0001, now + .16);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + .18);
  }

  /** 幕与幕之间：一阵缓和的湖风（带通噪声，自尾端渐隐） */
  whoosh() {
    if (!this.enabled) return;
    const context = this.ensureContext();
    if (!context) return;
    if (!this.noiseBuffer) {
      const length = Math.floor(context.sampleRate * 1.4);
      this.noiseBuffer = context.createBuffer(1, length, context.sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      for (let index = 0; index < length; index += 1) {
        data[index] = (Math.random() * 2 - 1) * (1 - index / length);
      }
    }
    const now = context.currentTime;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = this.noiseBuffer;
    filter.type = "bandpass";
    filter.Q.value = .8;
    filter.frequency.setValueAtTime(420, now);
    filter.frequency.exponentialRampToValueAtTime(1150, now + .5);
    filter.frequency.exponentialRampToValueAtTime(240, now + 1.25);
    gain.gain.setValueAtTime(.0001, now);
    gain.gain.exponentialRampToValueAtTime(.032, now + .35);
    gain.gain.exponentialRampToValueAtTime(.0001, now + 1.32);
    source.connect(filter).connect(gain).connect(context.destination);
    source.start(now);
    source.stop(now + 1.4);
  }

  playSeal() {
    if (!this.enabled) return;
    const context = this.ensureContext();
    if (!context) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(128, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(72, context.currentTime + .11);
    gain.gain.setValueAtTime(.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(.075, context.currentTime + .008);
    gain.gain.exponentialRampToValueAtTime(.0001, context.currentTime + .14);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + .15);
  }

  dispose() { void this.context?.close(); this.context = null; }

  private ensureContext() {
    if (!("AudioContext" in window)) return null;
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === "suspended") void this.context.resume();
    return this.context;
  }
}
